import { Check, AlertCircle } from 'lucide-react';
import type { EmailSendTranslations } from './types';

interface RegisteredEmailOptionProps {
  isSelected: boolean;
  registeredEmail?: string;
  isSending: boolean;
  t: EmailSendTranslations;
  onSelect: () => void;
}

export function RegisteredEmailOption({
  isSelected,
  registeredEmail,
  isSending,
  t,
  onSelect
}: RegisteredEmailOptionProps) {
  return (
    <button
      onClick={onSelect}
      disabled={!registeredEmail || isSending}
      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
        isSelected
          ? 'border-primary bg-primary/10'
          : 'border-border hover:border-primary/50'
      } ${!registeredEmail ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="font-medium text-foreground">
            {t.useRegisteredEmail}
          </p>
          {registeredEmail ? (
            <p className="text-sm text-muted-foreground mt-1">
              {registeredEmail}
            </p>
          ) : (
            <p className="text-sm text-destructive mt-1">
              {t.noRegisteredEmail}
            </p>
          )}
        </div>
        {isSelected && (
          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0 ml-3">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
    </button>
  );
}

interface NewEmailOptionProps {
  isSelected: boolean;
  email: string;
  isValidEmail: boolean;
  isSending: boolean;
  t: EmailSendTranslations;
  inputRef: React.RefObject<HTMLInputElement>;
  onSelect: () => void;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function NewEmailOption({
  isSelected,
  email,
  isValidEmail,
  isSending,
  t,
  inputRef,
  onSelect,
  onEmailChange
}: NewEmailOptionProps) {
  return (
    <div
      className={`w-full rounded-lg border-2 transition-all ${
        isSelected
          ? 'border-primary bg-primary/10'
          : 'border-border hover:border-primary/50'
      }`}
    >
      <button
        onClick={onSelect}
        disabled={isSending}
        className="w-full p-4 text-left"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="font-medium text-foreground">
              {t.useNewEmail}
            </p>
          </div>
          {isSelected && (
            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0 ml-3">
              <Check className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
      </button>

      <div className="px-4 pb-4">
        <input
          ref={inputRef}
          type="email"
          inputMode="email"
          value={email}
          onChange={onEmailChange}
          placeholder={t.emailPlaceholder}
          disabled={isSending || !isSelected}
          className={`w-full px-3 py-2 text-sm rounded-md border transition-all
            ${isSelected ? 'bg-background' : 'bg-muted'}
            ${!isValidEmail && email
              ? 'border-red-500 focus:ring-red-500'
              : 'border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
            }
            text-foreground
            placeholder:text-muted-foreground
            disabled:opacity-50 disabled:cursor-not-allowed
            outline-none
          `}
        />
        {!isValidEmail && email && (
          <p className="text-xs text-destructive mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {t.invalidEmail}
          </p>
        )}
      </div>
    </div>
  );
}
